import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideRouter, TitleStrategy, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import {
  atAuthInterceptor,
  AUTH_CONFIG,
  AUTH_DICTIONARY,
  AuthConfig,
  ConfigurableAuthService,
  DEFAULT_AUTH_DICTIONARY,
  PageVisibilityService,
  provideAuthenticatedRedirectUrl,
  provideErrorDictionary,
} from '@anothertrip/anothertrip-ds';

import { environment } from '../environments/environment.development';

import { provideAssistanceRequestsApiConfig } from './api/assistance-requests/assistance-requests-api.config';
import { provideAutopilotApiConfig } from './api/autopilot/autopilot-api.config';
import { provideBillingAccountApiConfig } from './api/billing-account/billing-account-api.config';
import { provideBlockedCreatorsApiConfig } from './api/blocked-creators/blocked-creators-api.config';
import { provideBrandingApiConfig } from './api/branding/branding-api.config';
import { provideBrandsApiConfig } from './api/brands/brands-api.config';
import { provideCampaignOutreachApiConfig } from './api/campaign-outreach/campaign-outreach-api.config';
import { provideCampaignsApiConfig } from './api/campaigns/campaigns-api.config';
import { provideCollaborationApiConfig } from './api/collaboration/collaboration-api.config';
import { provideComplianceApiConfig } from './api/compliance/compliance-api.config';
import { provideCreatorDiscoveryApiConfig } from './api/creator-discovery/creator-discovery-api.config';
import { provideDiscoveredCreatorsApiConfig } from './api/discovered-creators/discovered-creators-api.config';
import { provideDmVettingApiConfig } from './api/dm-vetting/dm-vetting-api.config';
import { provideHashtagWatchesApiConfig } from './api/hashtag-watches/hashtag-watches-api.config';
import { provideHomeSummaryApiConfig } from './api/home-summary/home-summary-api.config';
import { provideInvoiceApiConfig } from './api/invoice/invoice-api.config';
import { provideMapApiConfig } from './api/map/map-api.config';
import { provideProfileApiConfig } from './api/profile/profile-api.config';
import { provideSubscriptionsApiConfig } from './api/subscriptions/subscriptions-api.config';
import { provideTaxApiConfig } from './api/tax/tax-api.config';
import { routes } from './app.routes';
import { provideDialogDefaults } from './configs/dialog-defaults.config';
import { provideInsightsApiConfig } from './configs/insights-api.config';
import { provideInstagramApiConfig } from './configs/instagram-api.config';
import { provideMarketplaceApiConfig } from './configs/marketplace-api.config';
import { providePlacesApiConfig } from './configs/places-api.config';
import { providePrimaryRoute } from './configs/primary-route.config';
import { OutreachInboxService } from './features/campaigns/services/outreach-inbox.service';
import { instagramInterceptor } from './interceptors/instagram/instagram.interceptor';
import { paywallInterceptor } from './paywall/interceptors/paywall.interceptor';
import { PaywallService } from './paywall/services/paywall.service';
import { AppTitleStrategy } from './routes/app-title.strategy';
import { CookieConsentService } from './services/cookie-consent/cookie-consent.service';
import { InstagramOAuthCallbackService } from './services/instagram-oauth-callback.service';
import { LogoutService } from './services/logout.service';
import { ScrollTopOnNavigationService } from './services/scroll-top-on-navigation.service';
import { UserService } from './services/user/user.service';

const authConfig: AuthConfig = {
  apiBaseUrl: `${environment.operatorApiBaseUrl}/v1/operator`,
  endpoints: {
    login: 'login',
    requestPasswordReset: 'request-password-reset',
    resetPasswordNewUser: 'reset-password-new-user',
    waitlist: 'waitlist',
    logout: 'logout',
    instagramOAuth: 'instagram/oauth',
    userInfo: 'user-info',
    refreshToken: 'refresh-token',
    confirmCode: 'confirm-code',
    changePassword: 'change-password',
    sendConfirmationCode: 'send-confirmation-code',
    deleteAccount: 'delete-account',
  },
  customHeaders: {
    'client-id': environment.operatorClientId,
  },
  unauthenticatedRedirectRoute: '/login',
  bypassTokenEndpoints: [
    '/login',
    '/register',
    '/public-api',
    '/confirm-code',
    '/reset-password',
    '/request-password-reset',
    '/waitlist',
    '/send-confirmation-code',
    '/oauth2/code',
    '/oauth2/token',
    'cloudfunctions.net',
    'unsplash.com',
    'facebook.com',
  ],
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      // Fragment links (`/campaigns#autopilot`) scroll to their target. `scrollPositionRestoration`
      // is deliberately left off: `RouterScroller` treats a query-param-only navigation as a new
      // page and scrolls to [0, 0], which is exactly what the campaign detail and billing tab
      // switches are (`campaign-creators-tabs.component.ts`, `billing.page.ts`) — changing tab would
      // throw the operator back to the top of the page. The half of restoration a page change DOES
      // want — starting at the top — is done by hand in `ScrollTopOnNavigationService`, which
      // scrolls only when the path itself changed.
      withInMemoryScrolling({ anchorScrolling: 'enabled' })
    ),
    // Every page names itself in the document title: `{route title} | AnotherTrip Operator`.
    { provide: TitleStrategy, useClass: AppTitleStrategy },
    provideHttpClient(withFetch(), withInterceptors([atAuthInterceptor, paywallInterceptor, instagramInterceptor])),
    {
      provide: AUTH_CONFIG,
      useValue: authConfig,
    },
    {
      provide: AUTH_DICTIONARY,
      useValue: DEFAULT_AUTH_DICTIONARY,
    },
    provideAppInitializer(() => {
      // Must run before the router's initial navigation — see the service docs for
      // why the Instagram code cannot be read from ActivatedRoute.
      inject(InstagramOAuthCallbackService).initialize();
      // Before it too: the initial navigation must not carry a /#cookie-settings fragment (AT-2749).
      inject(CookieConsentService).consumeSettingsLink();
      const userService = inject(UserService);
      const paywallService = inject(PaywallService);
      const outreachInboxService = inject(OutreachInboxService);
      inject(PageVisibilityService).initializeVisibilityService();
      inject(LogoutService).initialize();
      inject(ScrollTopOnNavigationService).initialize();

      // Without Remember me a link opened in a new tab takes its session from an open tab; read the user after that
      return inject(ConfigurableAuthService).sessionRestored.then(() => {
        userService.loadUser();
        paywallService.initialize();
        outreachInboxService.initialize();
      });
    }),
    provideErrorDictionary(),
    provideDialogDefaults(),
    provideInstagramApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/instagram`,
      endpoints: {
        syncMedia: 'media/sync',
        listMedia: 'media',
        autoRespond: 'media/auto-respond',
        autoResponses: (mediaId) => `media/${mediaId}/auto-responses`,
        createAutoResponse: (mediaId) => `media/${mediaId}/auto-responses`,
        updateAutoResponse: (mediaId, responseId) => `media/${mediaId}/auto-responses/${responseId}`,
        deleteAutoResponse: (mediaId, responseId) => `media/${mediaId}/auto-responses/${responseId}`,
        defaultAutoResponses: 'auto-responses',
        createDefaultAutoResponse: 'auto-responses',
        updateDefaultAutoResponse: (responseId) => `auto-responses/${responseId}`,
        deleteDefaultAutoResponse: (responseId) => `auto-responses/${responseId}`,
        toggleCampaignMatching: (mediaId) => `media/${mediaId}/campaign-matching/toggle`,
        updateCampaignMatchingKeywords: (mediaId) => `media/${mediaId}/campaign-matching/keywords`,
        linkCampaignToPost: (mediaId, campaignId) => `media/${mediaId}/campaign-matching/campaign/${campaignId}`,
        unlinkCampaignFromPost: (mediaId, campaignId) => `media/${mediaId}/campaign-matching/campaign/${campaignId}`,
        getCampaignMatchingDetails: (mediaId) => `media/${mediaId}/campaign-matching`,
      },
    }),
    provideMapApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/maps`,
      endpoints: {
        generate: 'generate',
        status: (requestId) => `${requestId}/status`,
        activities: (requestId) => `${requestId}/activities`,
        createActivity: (requestId) => `${requestId}/activities`,
        updateActivity: (requestId, activityId) => `${requestId}/activities/${activityId}`,
        deleteActivity: (requestId, activityId) => `${requestId}/activities/${activityId}`,
        activityPhotos: (requestId, activityId) => `${requestId}/activities/${activityId}/photos`,
        updateActivityVisibility: (requestId, activityId) => `${requestId}/activities/${activityId}/visibility`,
        reorderActivities: (requestId) => `${requestId}/activities/order`,
        regenerateMap: (requestId) => `${requestId}/regenerate`,
        deleteMap: (requestId) => `${requestId}`,
        getBusinesses: 'businesses',
        createBusiness: 'businesses',
        updateBusiness: (businessId) => `businesses/${businessId}`,
        deleteBusiness: (businessId) => `businesses/${businessId}`,
        getBusinessPhotos: (businessId) => `businesses/${businessId}/photos`,
        updateBusinessVisibility: (businessId) => `businesses/${businessId}/visibility`,
      },
    }),
    provideInsightsApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/insights`,
      endpoints: {
        followersGrowth: 'instagram/followers-growth',
        summary: 'summary',
      },
    }),
    providePlacesApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/places`,
      endpoints: {
        searchPlaces: 'search',
      },
    }),
    provideMarketplaceApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/marketplace`,
      endpoints: {
        getOptIn: 'opt-in',
        updateOptIn: 'opt-in',
      },
    }),
    provideTaxApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/tax`,
      endpoints: {
        getProfile: 'profile',
        updateProfile: 'profile',
        getSupportedCountries: 'supported-countries',
      },
    }),
    provideCollaborationApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/collaborations`,
      endpoints: {
        getCollaborations: '',
        getCollaboration: (collaborationId) => `${collaborationId}`,
        createCollaboration: '',
        updateCollaboration: (collaborationId) => `${collaborationId}`,
        acceptCollaboration: (collaborationId) => `${collaborationId}/accept`,
        updateCollaborationStatus: (collaborationId) => `${collaborationId}/status`,
        getBenefitTypes: 'benefit-types',
        generateCollaborationInvoices: (collaborationId) => `${collaborationId}/invoices`,
        addCollaborationEvidence: (collaborationId) => `${collaborationId}/evidence`,
        updateCollaborationEvidence: (collaborationId, evidenceId) => `${collaborationId}/evidence/${evidenceId}`,
        deleteCollaborationEvidence: (collaborationId, evidenceId) => `${collaborationId}/evidence/${evidenceId}`,
        recheckEvidenceCompliance: (collaborationId, evidenceId) =>
          `${collaborationId}/evidence/${evidenceId}/recheck-compliance`,
      },
    }),
    provideComplianceApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/tax-audit`,
      endpoints: {
        scanCreatorPosts: 'scan',
        getRecords: 'records',
        updateStatus: 'status',
        getSummary: 'summary',
        getComplianceHealth: 'compliance-health',
        getComplianceInfo: 'compliance-info',
        requestAudit: 'request',
        getJobStatus: (jobId) => `jobs/${jobId}`,
        generateReport: 'report',
      },
    }),
    provideInvoiceApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/invoices`,
      endpoints: {
        getInvoice: (invoiceId) => `${invoiceId}`,
        getInvoices: '',
        createInvoice: '',
      },
    }),
    provideSubscriptionsApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/subscriptions`,
      endpoints: {
        getSubscriptions: '',
        getActiveSubscription: 'active',
        createSubscription: 'create',
        cancelSubscription: (subscriptionId) => `${subscriptionId}`,
        getPricingPlans: 'pricing-plans',
        getSubscription: (subscriptionId) => `${subscriptionId}`,
        getSubscriptionStatus: 'status',
        validatePromotionCode: 'promotion-code/validate',
      },
    }),
    provideCreatorDiscoveryApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/creator-discovery`,
      endpoints: {
        getCreatorsByHashtags: 'creators',
        getPostsByHashtag: (hashtag: string | number) => `hashtags/${hashtag}/posts`,
        createAssistanceRequest: 'assistance-requests',
        getAssistanceRequests: 'assistance-requests',
        cancelAssistanceRequest: (requestId: string | number) => `assistance-requests/${requestId}`,
        submitAssistanceRequestFeedback: (requestId: string | number) => `assistance-requests/${requestId}/feedback`,
        getAssistanceRequestCreators: (requestId: string | number) => `assistance-requests/${requestId}/creators`,
        getCreatorByHandle: 'creators/search',
        getCreatorHandleSearchStatus: 'creators/status',
        getCreatorHandleSearch: (searchId: string | number) =>
          `creators/status/${encodeURIComponent(String(searchId))}`,
        requestLinkedAccounts: (fbid: string | number) =>
          `creators/${encodeURIComponent(String(fbid))}/linked-accounts`,
        getLinkedAccounts: (fbid: string | number) => `creators/${encodeURIComponent(String(fbid))}/linked-accounts`,
        refreshCreatorSignals: 'creator-signals/refresh',
      },
    }),
    provideAssistanceRequestsApiConfig(`${environment.operatorApiBaseUrl}/v1/operator/creator-discovery`),
    provideHashtagWatchesApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/creator-discovery`,
      endpoints: {
        listWatches: 'hashtag-watches',
        createWatch: 'hashtag-watches',
        getCountrySummary: 'hashtag-watches/country-summary',
        getOpsSummary: 'hashtag-watches/ops-summary',
        suggestHashtags: 'suggest-hashtags',
        getSettings: 'hashtag-watches/settings',
        updateSettings: 'hashtag-watches/settings',
        getWatch: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}`,
        getSweeps: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}/sweeps`,
        updateWatch: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}`,
        deleteWatch: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}`,
        pauseWatch: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}/pause`,
        resumeWatch: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}/resume`,
        getAlerts: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}/alerts`,
        dismissAlerts: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}/alerts/dismiss`,
        restoreAlerts: (hashtag) => `hashtag-watches/${encodeURIComponent(String(hashtag))}/alerts/restore`,
      },
    }),
    provideBlockedCreatorsApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/creator-discovery`,
      endpoints: {
        blockCreator: 'blocked-creators',
        listBlockedCreators: 'blocked-creators',
        unblockCreator: (fbid) => `blocked-creators/${encodeURIComponent(String(fbid))}`,
      },
    }),
    provideDiscoveredCreatorsApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/discovered-creators`,
      endpoints: {
        getDiscoveredCreators: '',
        saveCreator: 'save',
        unsaveCreator: 'unsave',
        getDiscoveredCreator: (fbid) => `${fbid}`,
        getDiscoveredCreatorPosts: (fbid) => `${fbid}/posts`,
        updateDiscoveredCreator: (fbid) => `${fbid}`,
        bulkTagCreators: 'bulk-tag',
        bulkTagFiltered: 'bulk-tag-filtered',
        addCreatorNote: (fbid) => `${fbid}/notes`,
        deleteCreatorNote: (fbid, noteId) => `${fbid}/notes/${noteId}`,
        refreshAvatars: 'refresh-avatars',
      },
    }),
    provideHomeSummaryApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/home-summary`,
      endpoints: {
        getHomeSummary: '',
      },
    }),
    provideAutopilotApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/autopilot`,
      endpoints: {
        generate: 'generate',
        select: 'select',
        getStatus: 'status',
        reset: 'reset',
      },
    }),
    provideCampaignsApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/campaigns`,
      endpoints: {
        getCampaigns: '',
        getCampaign: (campaignId) => `${campaignId}`,
        createCampaign: '',
        updateCampaign: (campaignId) => `${campaignId}`,
        deleteCampaign: (campaignId) => `${campaignId}`,
        getCampaignStats: (campaignId) => `${campaignId}/stats`,
        getCampaignEvidence: (campaignId) => `${campaignId}/evidence`,
        scanCampaignEvidence: (campaignId) => `${campaignId}/evidence/scan`,
        scanCampaignCreatorEvidence: (campaignId, creatorId) => `${campaignId}/evidence/scan/${creatorId}`,
        addCampaignEvidence: (campaignId, creatorId) => `${campaignId}/creators/${creatorId}/evidence`,
        removeCampaignEvidence: (campaignId, creatorId, evidenceId) =>
          `${campaignId}/creators/${creatorId}/evidence/${evidenceId}`,
        updateCampaignCreator: (campaignId, creatorId) => `${campaignId}/creators/${creatorId}`,
        findMatchingCreators: (campaignId) => `${campaignId}/find-matching-creators`,
        getAIReports: (campaignId) => `${campaignId}/ai-reports`,
        getAIReportMatches: (campaignId, reportId) => `${campaignId}/ai-reports/${reportId}/matches`,
        getMatchPostMedia: (campaignId, reportId, creatorId) =>
          `${campaignId}/ai-reports/${reportId}/matches/${creatorId}/media`,
        analyzeCreators: (campaignId) => `${campaignId}/analyze-creators`,
        matchIndividualCreators: (campaignId) => `${campaignId}/match-individual-creators`,
        getIndividualCreatorResults: (campaignId) => `${campaignId}/individual-creator-results`,
        updateCampaignStatus: (campaignId) => `${campaignId}/status`,
        getCampaignApplications: (campaignId) => `${campaignId}/applications`,
        getCampaignApplication: (campaignId, creatorId) => `${campaignId}/applications/${creatorId}`,
        reviewApplication: (campaignId, creatorId) => `${campaignId}/applications/${creatorId}/review`,
        addCreatorsToCampaign: (campaignId) => `${campaignId}/creators`,
        getCampaignCreators: (campaignId) => `${campaignId}/creators`,
        getCampaignCreatorMaps: (campaignId) => `${campaignId}/creator-maps`,
        getCampaignBookingStats: (campaignId) => `${campaignId}/booking-stats`,
        removeCampaignCreator: (campaignId, creatorId) => `${campaignId}/creators/${creatorId}`,
        getCampaignImageUploadUrl: (campaignId) => `${campaignId}/images/upload-url`,
        confirmCampaignImageUpload: (campaignId) => `${campaignId}/images/confirm`,
        deleteCampaignImage: (campaignId, imageId) => `${campaignId}/images/${imageId}`,
        updateCampaignImage: (campaignId, imageId) => `${campaignId}/images/${imageId}`,
        reorderCampaignImages: (campaignId) => `${campaignId}/images/order`,
        linkCampaignImages: (campaignId) => `${campaignId}/images/link`,
        linkCampaignHero: (campaignId) => `${campaignId}/images/link`,
        setCampaignNextStep: (campaignId, stepId) => `${campaignId}/next-steps/${stepId}`,
        addBookingLink: (campaignId) => `${campaignId}/booking-links`,
        updateBookingLink: (campaignId, linkId) => `${campaignId}/booking-links/${linkId}`,
        removeBookingLink: (campaignId, linkId) => `${campaignId}/booking-links/${linkId}`,
        setWholeTripLink: (campaignId) => `${campaignId}/whole-trip-link`,
        setFeaturedLink: (campaignId) => `${campaignId}/featured-link`,
        addCampaignInspirationPost: (campaignId) => `${campaignId}/inspiration-posts`,
        removeCampaignInspirationPost: (campaignId, inspirationId) =>
          `${campaignId}/inspiration-posts/${inspirationId}`,
        getAnnouncement: (campaignId) => `${campaignId}/announcement`,
        createAnnouncementPost: (campaignId) => `${campaignId}/announcement`,
        setAnnouncementBackground: (campaignId) => `${campaignId}/announcement/background`,
        updateAnnouncementPost: (campaignId, postId) => `${campaignId}/announcement/posts/${postId}`,
        approveAnnouncementPost: (campaignId, postId) => `${campaignId}/announcement/posts/${postId}/approve`,
        dismissAnnouncementPost: (campaignId, postId) => `${campaignId}/announcement/posts/${postId}/dismiss`,
        regenerateAnnouncementPost: (campaignId, postId) => `${campaignId}/announcement/posts/${postId}/regenerate`,
        updateAnnouncement: (campaignId) => `${campaignId}/announcement`,
        approveAnnouncement: (campaignId) => `${campaignId}/announcement/approve`,
        dismissAnnouncement: (campaignId) => `${campaignId}/announcement/dismiss`,
        regenerateAnnouncement: (campaignId) => `${campaignId}/announcement/regenerate`,
      },
    }),
    provideBrandingApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/branding`,
      endpoints: {
        getBranding: '',
        updateBranding: '',
        getLogoUploadUrl: 'logo-upload-url',
      },
    }),
    provideProfileApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/profile`,
      endpoints: {
        getProfile: '',
        updateProfile: '',
      },
    }),
    provideBrandsApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/brands`,
      endpoints: {
        list: '',
        create: '',
        get: (brandId) => `${brandId}`,
        update: (brandId) => `${brandId}`,
        delete: (brandId) => `${brandId}`,
        listSources: (brandId) => `${brandId}/sources`,
        addSource: (brandId) => `${brandId}/sources`,
        deleteSource: (brandId, sourceId) => `${brandId}/sources/${sourceId}`,
        startCrawl: (brandId, sourceId) => `${brandId}/sources/${sourceId}/crawl`,
        listAssets: (brandId) => `${brandId}/assets`,
        reviewAssets: (brandId) => `${brandId}/assets/review`,
        purgeAsset: (brandId, assetId) => `${brandId}/assets/${assetId}`,
        assetUploadUrl: (brandId) => `${brandId}/assets/upload-url`,
        confirmAsset: (brandId) => `${brandId}/assets/confirm`,
        getProfile: (brandId) => `${brandId}/profile`,
        updateProfile: (brandId) => `${brandId}/profile`,
        analyzeProfile: (brandId) => `${brandId}/profile/analyze`,
        acceptProfileCandidate: (brandId) => `${brandId}/profile/candidate/accept`,
        dismissProfileCandidate: (brandId) => `${brandId}/profile/candidate/dismiss`,
        listCampaigns: (brandId) => `${brandId}/campaigns`,
      },
    }),
    provideDmVettingApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator`,
      endpoints: {
        getSetting: 'dm-vetting',
        updateSetting: 'dm-vetting',
        disableSetting: 'dm-vetting',
        getReport: (campaignId) => `campaigns/${campaignId}/dm-vetting/report`,
      },
    }),
    provideCampaignOutreachApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/campaigns`,
      endpoints: {
        composeOutreach: (campaignId) => `${campaignId}/outreach/compose`,
        saveOutreachCompose: (campaignId) => `${campaignId}/outreach/compose/save`,
        sendOutreach: (campaignId) => `${campaignId}/outreach/send`,
        markDmSent: (campaignId, creatorId) => `${campaignId}/outreach/dm/${creatorId}/mark-sent`,
        skipDm: (campaignId, creatorId) => `${campaignId}/outreach/dm/${creatorId}/skip`,
        getOutreachStatus: (campaignId) => `${campaignId}/outreach/status`,
        getOutreachSends: (campaignId, creatorId) => `${campaignId}/outreach/sends/${creatorId}`,
        // Two-way conversation feature.
        getOutreachConversation: (campaignId, creatorId) => `${campaignId}/outreach/conversation/${creatorId}`,
        sendOperatorReply: (campaignId, creatorId) => `${campaignId}/outreach/replies/${creatorId}`,
        // Account-level reads — not scoped to a campaign.
        getOutreachUsage: 'outreach/usage',
        getOutreachInbox: 'outreach/inbox',
      },
    }),
    provideBillingAccountApiConfig({
      baseUrl: `${environment.operatorApiBaseUrl}/v1/operator/billing`,
      endpoints: {
        getPaymentMethod: 'payment-method',
        updatePaymentMethod: 'payment-method',
        createSetupIntent: 'payment-method/setup-intent',
        getBillingEmail: 'email',
        updateBillingEmail: 'email',
      },
    }),
    provideNativeDateAdapter(),
    providePrimaryRoute('/dashboard/home'),
    provideAuthenticatedRedirectUrl('/dashboard/home'),
  ],
};
